import { Fragment, useState, useEffect, useMemo } from 'react'
import { format, isValid } from 'date-fns'
import clsx from 'clsx'
import { Icon } from '@iconify/react'

import {
  Spin,
  DisplayBrief,
  Divider,
  SharedProperties,
  useUtils,
  useGetBriefContent,
} from '@pepper/ui'
import { getReadableAmount } from '@pepper/utils/src/function/currency'

import { useSelectedCompanyId } from 'utils/functions/misc'

import styles from './_.module.css'

interface FieldKeys {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
  title: string
  type: string
  id?: string
  options?: { label: string; value: number | string }[] | BlockSelectOption[]
}

interface AssignmentBriefProps {
  projectBrief: FieldKeys[]
  assignmentBrief: FieldKeys[]
  data: Assignment
  isPage?: boolean
  loomHeight?: number
  loomWidth?: number
}

interface BriefDetails {
  assignment: FieldKeys[]
  project: FieldKeys[]
}

const AssignmentBrief = ({
  data: assignmentDetails,
  isPage = true,
  projectBrief,
  assignmentBrief,
  loomHeight,
  loomWidth,
}: AssignmentBriefProps): JSX.Element => {
  const [briefDetails, setBriefDetails] = useState<BriefDetails>({
    assignment: [],
    project: [],
  })
  const [assignment, setAssignment] = useState<Assignment>()
  const [loading, setLoading] = useState(true)
  const { sub_verticals } = useUtils()
  const briefContent = useGetBriefContent({
    data: { pepperDocId: assignment?.document_brief?.pepper_doc_id },
  })
  const companyId = useSelectedCompanyId()

  const basicAssignmentDetails: FieldKeys[] = useMemo(() => {
    const dateAssigned = {
      title: 'Date Assigned',
      type: 'text',
      value:
        typeof assignment?.date_created === 'string' &&
        isValid(new Date(assignment.date_created))
          ? format(new Date(assignment.date_created), 'dd-MM-yyyy | hh:mm a')
          : 'N/A',
    }

    const amount = {
      title: 'Assignment Amount',
      value: assignment?.assignment_amount
        ? getReadableAmount(assignment?.assignment_amount)
        : 'N/A',
      type: 'text',
    }

    const vertical = {
      title: 'Vertical',
      value: assignment?.vertical?.name ?? 'N/A',
      type: 'text',
    }

    if (assignment?.category?.key === 'translation') {
      const sourceFile = {
        title: 'Source File',
        value: assignment?.translation_file
          ? [assignment?.translation_file]
          : [],
        type: 'files',
      }

      const sourceLanguage = {
        title: 'Source Language',
        type: 'text',
        value: assignment?.translation_language_source?.name ?? '',
      }

      const targetLanguage = {
        title: 'Target Language',
        type: 'text',
        value: assignment?.translation_language_target?.name ?? '',
      }

      const translationType = {
        title: 'Translation Type',
        type: 'text',
        value: assignment?.translation_type?.name ?? 'N/A',
      }

      const translationUsage = {
        title: 'Translation Usage',
        type: 'text',
        value: assignment?.translation_word_usage?.name ?? 'N/A',
      }

      return [
        dateAssigned,
        amount,
        sourceLanguage,
        targetLanguage,
        translationType,
        translationUsage,
        sourceFile,
      ]
    }

    return [dateAssigned, vertical, amount]
  }, [assignment])

  const basicProjectDetails: FieldKeys[] = useMemo(() => {
    const projectId = {
      title: 'Project ID',
      type: 'text',
      value: assignment?.project?.code ?? 'N/A',
    }

    const projectTitle = {
      title: 'Project Title',
      type: 'text',
      value: assignment?.project?.title ?? 'N/A',
    }

    return [projectId, projectTitle]
  }, [assignment])

  useEffect(() => {
    setBriefDetails({
      assignment: [
        ...basicAssignmentDetails,
        ...assignmentBrief,
        ...projectBrief,
      ],
      project: [],
    })
    setLoading(false)
  }, [
    assignmentBrief,
    basicAssignmentDetails,
    basicProjectDetails,
    projectBrief,
  ])

  useEffect(() => {
    if (assignmentDetails) {
      const _data = { ...assignmentDetails }

      if (Object.prototype.hasOwnProperty.call(_data, 'document_template_id')) {
        _data.document_template_id = _data?.document_template?.template_name
      }

      _data.reference_files_design_content = {
        comment: assignment?.design_content || '',
        attachments:
          assignment?.reference_files?.filter(file => file.tag === 'content') ||
          [],
      }

      _data.reference_files_other_references = {
        comment: assignment?.design_other_references,
        attachments:
          assignment?.reference_files?.filter(
            file => file.tag === 'other_references'
          ) || [],
      }

      _data.reference_files_infographic_description = {
        comment: assignment?.design_infographic_description || '',
        attachments:
          assignment?.reference_files?.filter(
            file => file.tag === 'infographic_description'
          ) || [],
      }

      _data.adapts_main_design = {
        comment: assignment?.design_adapts_main_design || '',
        attachments:
          assignment?.reference_files?.filter(
            file => file.tag === 'adapt_main_design'
          ) || [],
      }

      _data.reference_files_artworks = assignment?.reference_files?.filter(
        file => file.tag === 'artworks'
      )

      _data.other_information = {
        comment:
          typeof _data?.other_information === 'string'
            ? _data?.other_information
            : '',
        attachments:
          assignment?.reference_files?.filter(
            file => file.tag === 'other_information_tag'
          ) || [],
      }

      _data.outline_structure = {
        comment:
          typeof _data?.outline_structure === 'string'
            ? _data?.outline_structure
            : '',
        attachments:
          assignment?.reference_files?.filter(
            file => file.tag === 'outline_structure'
          ) || [],
      }

      _data.other_instructions = {
        comment:
          typeof _data?.other_instructions === 'string'
            ? _data?.other_instructions
            : '',
        attachments:
          assignment?.reference_files?.filter(
            file => file.tag === 'other_instructions_tag'
          ) || [],
      } as string | CommentBox

      /* only for design category project */
      const _logoFiles = {
        comment: _data?.design_references_logo_files,
        attachments: _data?.references?.filter(
          (file: Attachment) => file.tag === 'logo_files'
        ),
      }

      const _styleBrandGuide = {
        comment: _data?.design_references_brand_style_guide,
        attachments: _data?.references?.filter(
          (file: Attachment) => file.tag === 'brand_style_guide'
        ),
      }

      const _adapts_main_design = {
        comment: _data?.design_adapts_main_design ?? '',
        attachments: _data?.reference_files.filter(
          (file: Attachment) => file.tag === 'adapt_main_design'
        ),
      }
      _data.adapts_main_design = _adapts_main_design

      _data.references_logo_files = _logoFiles
      _data.references_brand_style_guide = _styleBrandGuide
      setAssignment(_data)
    }
  }, [
    assignment?.design_adapts_main_design,
    assignment?.design_content,
    assignment?.design_infographic_description,
    assignment?.design_other_references,
    assignment?.reference_files,
    assignmentDetails,
  ])

  const openBrief = (briefId?: string, pepperDocId?: string) => {
    if (!briefId) return
    window.open(`/${companyId}/docs/${pepperDocId}?context_id=${briefId}`)
  }

  // get formatted details
  const getFormattedAssignmentDetails = () => {
    const _formattedDetails: FieldKeys[][] = []
    let _keywordsFields: FieldKeys[] = []
    briefDetails.assignment.forEach(detail => {
      if (
        detail?.type === 'comment-box' &&
        detail?.value &&
        !detail?.value?.comment
      ) {
        detail.value = {
          comment: detail.value,
        }
      }
      if (detail.id === 'translation_languages_target_id') {
        detail.value = assignment?.translation_language_target?.id
      }
      if (detail.id === 'sub_vertical_id') {
        _formattedDetails.push([
          {
            ...detail,
            options: sub_verticals.map(subVertical => {
              return {
                label: subVertical.title,
                value: subVertical.id,
              }
            }),
          },
        ])
        return
      }
      _formattedDetails.push([detail])

      if (_keywordsFields.length === 2) {
        _formattedDetails.push([..._keywordsFields])
        _keywordsFields = []
      }
    })
    return _formattedDetails
  }

  return (
    <div
      className={clsx(styles.assignmentBriefDetails, {
        [styles.pageContainer]: isPage,
      })}
    >
      <Spin spinning={loading}>
        <div className={styles.wrapper}>
          <DisplayBrief
            data={assignment}
            fields={getFormattedAssignmentDetails()}
            loomHeight={loomHeight}
            loomWidth={loomWidth}
          />
        </div>
        {typeof assignment !== 'undefined' &&
          Array.isArray(assignment?.shared_properties) &&
          assignment?.shared_properties.length > 0 && (
            <Fragment>
              <Divider className={styles.divider} type="horizontal" />
              <SharedProperties
                assignment={assignment}
                portal="business"
                companyId={assignment.project.company?.id}
              />
            </Fragment>
          )}
        {briefContent.data && (
          <Fragment>
            <div className={styles.separation}>
              <div className={styles.briefTitle}>
                <Icon icon="uil:file-alt" className={styles.fileIcon} />
                Creative brief: {assignment?.document_brief?.title}
              </div>
              <div
                className={styles.extLink}
                role="none"
                onClick={() =>
                  openBrief(
                    assignment?.document_brief?.id,
                    assignment?.document_brief?.pepper_doc_id
                  )
                }
              >
                <Icon icon="tabler:external-link" className={styles.extIcon} />{' '}
                Open in docs
              </div>
            </div>
            <div className={styles.textBody}>
              <div className={styles.textHeader}>
                Creative brief - {assignment?.document_brief?.title}
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: briefContent.data?.content.html as string,
                }}
                className={styles.textContent}
              />
            </div>
          </Fragment>
        )}
      </Spin>
    </div>
  )
}

export default AssignmentBrief
